import React, { useState } from "react";

const DICT = {
    title: { en: "Save Your API Key", zh: "請儲存您的 API 金鑰" },
    warning: {
        en: "This key will never be shown again. Copy and store it somewhere safe before closing.",
        zh: "此金鑰不會再次顯示，請在關閉前複製並妥善保存。",
    },
    copy: { en: "Copy", zh: "複製" },
    copied: { en: "Copied!", zh: "已複製！" },
    done: { en: "I've saved it, close", zh: "已儲存，關閉" },
    channelLabel: { en: "Channel", zh: "頻道" },
};

const t = (key, lang) => DICT[key]?.[lang] || DICT[key]?.en || key;

export default function ApiKeyRevealModal({
    language,
    apiKey,
    channelName,
    onClose,
}) {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(apiKey);
        setCopied(true);
        setTimeout(() => setCopied(false), 2500);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
                {/* Header */}
                <div className="flex items-start justify-between px-6 pt-6 pb-4 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
                            <svg
                                className="w-4 h-4 text-green-600"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                            </svg>
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-gray-900">
                                {t("title", language)}
                            </h3>
                            <p className="text-sm text-gray-500">
                                <span className="font-medium text-gray-700">
                                    {t("channelLabel", language)}:
                                </span>{" "}
                                {channelName}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6">
                    {/* Warning banner */}
                    <div className="flex items-start gap-2 mb-5 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl">
                        <svg
                            className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                        >
                            <path
                                fillRule="evenodd"
                                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                                clipRule="evenodd"
                            />
                        </svg>
                        <p className="text-xs text-amber-700">
                            {t("warning", language)}
                        </p>
                    </div>

                    {/* Key display + copy */}
                    <div className="flex items-center gap-2 bg-gray-950 rounded-xl px-4 py-3">
                        <svg
                            className="w-4 h-4 text-gray-400 flex-shrink-0"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                            />
                        </svg>
                        <code className="text-sm text-green-400 font-mono flex-1 break-all select-all">
                            {apiKey}
                        </code>
                        <button
                            onClick={handleCopy}
                            className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 border ${
                                copied
                                    ? "bg-green-500/20 text-green-400 border-green-500/30"
                                    : "bg-white/10 text-gray-300 border-white/10 hover:bg-white/20"
                            }`}
                        >
                            {copied ? (
                                <>
                                    <svg
                                        className="w-3 h-3"
                                        fill="currentColor"
                                        viewBox="0 0 20 20"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                    {t("copied", language)}
                                </>
                            ) : (
                                <>
                                    <svg
                                        className="w-3 h-3"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                                        />
                                    </svg>
                                    {t("copy", language)}
                                </>
                            )}
                        </button>
                    </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end px-6 py-4 border-t border-gray-100 bg-gray-50/50">
                    <button
                        onClick={onClose}
                        className="px-5 py-2 text-sm font-medium text-white bg-orange-500 hover:bg-orange-600 rounded-xl shadow-sm transition-all"
                    >
                        {t("done", language)}
                    </button>
                </div>
            </div>
        </div>
    );
}
